import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from '../constants';
import { useHistory } from "react-router-dom";
import useNavigationTracker from 'src/hooks/Utils/useNavigationTracker';

interface NavigationHistoryContextType {
  pathHistory: string[];
  previousPage: string | null;
  currentPage: string;
  safeNavigateBack: (fallbackRoute?: string, excludeRoutes?: string[]) => void;
}

const NavigationHistoryContext = createContext<NavigationHistoryContextType>({
  pathHistory: [],
  previousPage: null,
  currentPage: '',
  safeNavigateBack: () => {}
});

export const NavigationHistoryProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const [pathHistory, setPathHistory] = useState<string[]>([]);
  const currentPageRef = useRef<string>(location.pathname);
  const previousPageRef = useRef<string | null>(null);
  useNavigationTracker();
  
  useEffect(() => {
    if (location.pathname !== currentPageRef.current) {
      previousPageRef.current = currentPageRef.current;
      currentPageRef.current = location.pathname;
      
      setPathHistory(prev => {
        const newHistory = [...prev];
        if (previousPageRef.current) {
          newHistory.push(previousPageRef.current);
          if (newHistory.length > 10) {
            newHistory.shift();
          }
        }
        return newHistory;
      });
    }
  }, [location.pathname]);

  const safeNavigateBack = (
    fallbackRoute: string = ROUTE_PATHS.PRODUCTS,
    excludeRoutes: string[] = []
  ): void => {
    if (previousPageRef.current && 
        !excludeRoutes.some(route => previousPageRef.current?.includes(route))) {
      history.goBack();
    } else {
      history.push(fallbackRoute);
    }
  };
  
  return (
    <NavigationHistoryContext.Provider 
      value={{
        pathHistory,
        previousPage: previousPageRef.current,
        currentPage: currentPageRef.current,
        safeNavigateBack,
      }}
    >
      {children}
    </NavigationHistoryContext.Provider>
  );
};

export const useNavigationHistory = () => useContext(NavigationHistoryContext);
